import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Capture errors fired BEFORE Sentry initializes (it's deferred to
// requestIdleCallback below, leaving a ~100-2000ms window where the most
// common error class — lazy-route chunk-load failures from the React.lazy
// imports in App.tsx — would otherwise go nowhere). Drained into Sentry
// once the SDK loads. Adversarial review H2 (cross-model consensus).
interface EarlyEvent {
  kind: 'error' | 'rejection';
  message: string;
  error?: Error;
  reason?: unknown;
  stack?: string;
}
const __earlyEvents: EarlyEvent[] = [];
const errorListener = (e: ErrorEvent) => {
  __earlyEvents.push({
    kind: 'error',
    message: e.message,
    error: e.error,
    stack: e.error?.stack,
  });
};
const rejectionListener = (e: PromiseRejectionEvent) => {
  const reason = e.reason;
  __earlyEvents.push({
    kind: 'rejection',
    message: reason instanceof Error ? reason.message : String(reason),
    reason,
    stack: reason instanceof Error ? reason.stack : undefined,
  });
};
window.addEventListener('error', errorListener);
window.addEventListener('unhandledrejection', rejectionListener);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Sentry init runs AFTER first paint to avoid blocking the critical render
// path. The SDK is ~70 KB gz on its own and pulls in a fair amount of work
// at construction (error handler shims, tracing integration, transport
// init). Deferring it to requestIdleCallback (or a 0ms setTimeout fallback)
// keeps the user's first-paint budget free for the actual application code.
//
// Errors fired BEFORE Sentry initializes are not captured. The trade is
// acceptable because:
//   1. The most common errors happen during interaction, not first render.
//   2. requestIdleCallback typically fires within ~100-300ms of paint.
//   3. The browser's built-in `window.onerror` would still log to console.
const initSentryDeferred = () => {
  const dsn = import.meta.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;
  // Async-import the SDK so it lands in its own chunk and only downloads
  // once the main app is interactive.
  Promise.all([
    import('@sentry/react'),
    import('./sentryFilters'),
  ]).then(([Sentry, { beforeSend }]) => {
    Sentry.init({
      dsn,
      environment: import.meta.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
      release: import.meta.env.REACT_APP_SENTRY_RELEASE || undefined,
      tracesSampleRate: Number(import.meta.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0),
      sendDefaultPii: false,
      beforeSend,
    });
    // Drain any errors that fired during the deferred-init window into Sentry,
    // tagged so we can filter them in the dashboard. The native listeners
    // can be removed now — Sentry installs its own GlobalHandlers integration.
    window.removeEventListener('error', errorListener);
    window.removeEventListener('unhandledrejection', rejectionListener);
    for (const evt of __earlyEvents) {
      const captured = evt.error ?? evt.reason;
      if (captured instanceof Error) {
        Sentry.captureException(captured, {
          tags: { early_capture: true, kind: evt.kind },
        });
      } else {
        Sentry.captureMessage(evt.message, {
          tags: { early_capture: true, kind: evt.kind },
          level: 'error',
        });
      }
    }
    __earlyEvents.length = 0;
    // Forward web-vitals into Sentry once the SDK is up. Each metric becomes
    // a breadcrumb so we can correlate them with errors that fire later in
    // the session.
    reportWebVitals((metric) => {
      Sentry.addBreadcrumb({
        category: 'web-vital',
        message: `${metric.name}: ${metric.value.toFixed(2)}`,
        level: 'info',
        data: { name: metric.name, value: metric.value, id: metric.id },
      });
    });
  }).catch(() => {
    // Sentry is best-effort — a failed dynamic import (offline, blocked CDN)
    // should never break the app. Early-error listeners stay attached so
    // window.onerror at least sees them.
  });
};

const scheduleSentry = (window as any).requestIdleCallback
  ? (cb: () => void) => (window as any).requestIdleCallback(cb, { timeout: 2000 })
  : (cb: () => void) => window.setTimeout(cb, 0);
scheduleSentry(initSentryDeferred);
